import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ShareTour } from "@/components/ShareTour";
import { BadgeShelf, MyBadges } from "@/components/Badges";
import { ReferralBoard } from "@/components/ReferralBoard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, Star, Gift, Download, Trash2, Users, UserPlus, Send } from "lucide-react";
import { toast } from "sonner";
import { inviteSummary, parseEmails, MAX_INVITES } from "@/lib/inviteReason";

const THEMES = [
  { id: "warm", label: "Electric Blue", swatch: "#1A7DFF" },
  { id: "forest", label: "Cyan Signal", swatch: "#22B8FF" },
  { id: "ink", label: "Pure Midnight", swatch: "#0A0A0B" },
];

// One labelled stretch of the page, so Settings reads top to bottom
const Section = ({ title, note, children, testId }) => (
  <section data-testid={testId} className="space-y-4">
    <div>
      <h2 className="font-display text-xl font-bold tracking-tight md:text-2xl">{title}</h2>
      {note && <p className="mt-1 text-sm text-ink-soft">{note}</p>}
    </div>
    {children}
  </section>
);

export default function Settings() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const [name, setName] = useState(user?.name || "");
  const [callMe, setCallMe] = useState(user?.call_me || "");
  const [theme, setTheme] = useState(user?.plan === "free" ? "warm" : user?.theme || "warm");
  const [fb, setFb] = useState({ category: "general", message: "", rating: 5 });
  const [history, setHistory] = useState([]);
  const [referrals, setReferrals] = useState(null);
  const [team, setTeam] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [confirmDelete, setConfirmDelete] = useState("");
  const [referralEmail, setReferralEmail] = useState("");
  const [invitingRef, setInvitingRef] = useState(false);
  const [emailReady, setEmailReady] = useState(false);

  const sendReferralInvite = async () => {
    const targets = parseEmails(referralEmail);
    if (!targets.length) return toast.error("Add at least one valid email address");
    if (targets.length > MAX_INVITES)
      return toast.error(`Up to ${MAX_INVITES} addresses at a time — you pasted ${targets.length}`);
    setInvitingRef(true);
    try {
      const { data } = await api.post("/referrals/invite", {
        emails: targets,
        origin_url: window.location.origin,
      });
      if (data.sent_count) setReferralEmail("");
      if (!data.sent_count) navigator.clipboard?.writeText(data.link);
      toast[data.sent ? "success" : "error"](
        data.sent
          ? `${inviteSummary(data, "invite")} — you both get Premium when they join`
          : inviteSummary(data, "invite"),
        { duration: data.sent ? 5000 : 9000 },
      );
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setInvitingRef(false);
    }
  };

  const refInviteCount = parseEmails(referralEmail).length;

  const loadTeam = () =>
    api.get("/teams/mine").then(({ data }) => setTeam(data.team ? data : null)).catch(() => {});

  useEffect(() => {
    api.get("/payments/history").then(({ data }) => setHistory(data)).catch(() => {});
    api.get("/me/referrals").then(({ data }) => setReferrals(data)).catch(() => {});
    api.get("/email/status").then(({ data }) => setEmailReady(data.configured)).catch(() => {});
    loadTeam();
  }, []);

  const exportData = async () => {
    const res = await api.get("/me/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "chatmilu-my-data.json";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Your data export has been downloaded");
  };

  const deleteAccount = async () => {
    if (confirmDelete !== "DELETE") return toast.error("Type DELETE to confirm");
    await api.delete("/me");
    localStorage.clear();
    toast.success("Everything we stored about you has been deleted");
    window.location.href = "/";
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await api.post("/teams/invite", { email: inviteEmail.trim(), origin_url: window.location.origin });
      setInviteEmail("");
      await loadTeam();
      toast.success(emailReady ? "Seat assigned — invite emailed" : "Seat assigned — copy the invite link below");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeMember = async (email) => {
    await api.delete(`/teams/members/${encodeURIComponent(email)}`);
    await loadTeam();
    toast.success("Seat released");
  };

  const save = async () => {
    try {
      await api.put("/me/preferences", { name, theme, call_me: callMe });
      await refresh();
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const sendFeedback = async () => {
    if (!fb.message.trim()) return;
    try {
      await api.post("/feedback", fb);
      setFb({ ...fb, message: "" });
      toast.success("Thanks — our support team will follow up by email.");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-12 px-5 py-12 sm:px-8" data-testid="settings-page">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tighter sm:text-4xl">
          {t("settings")}
        </h1>
        <p className="mt-2 text-sm text-ink-soft md:text-base">
          Everything about your account, in the order you usually need it.
        </p>
      </div>

      <Section title="You" note="Your name, your language, and how chat looks." testId="section-you">
        <div className="rounded-3xl border border-white/10 bg-panel p-8">
        <div className="mt-6 space-y-5">
          <div>
            <Label>Display name</Label>
            <Input
              data-testid="settings-name-input" value={name} onChange={(e) => setName(e.target.value)}
              className="mt-1.5 min-h-[44px] rounded-xl"
            />
          </div>
          <div>
            <Label>What MiLu should call you</Label>
            <Input
              data-testid="settings-callme-input" value={callMe}
              onChange={(e) => setCallMe(e.target.value)}
              placeholder="Michael"
              className="mt-1.5 min-h-[44px] rounded-xl"
            />
            <p className="mt-1.5 text-xs text-ink-soft">
              He greets you with this in the morning, at night and on calls.
            </p>
          </div>
          <div>
            <Label>{t("language")} (persisted to your account)</Label>
            <div className="mt-1.5">
              <LanguageSwitcher />
            </div>
          </div>
          <div>
            <Label>Chat theme {user?.plan === "free" && <span className="text-ink-soft">(paid plans)</span>}</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {THEMES.map((th) => (
                <button
                  key={th.id}
                  data-testid={`theme-${th.id}`}
                  disabled={user?.plan === "free" && th.id !== "warm"}
                  onClick={() => setTheme(th.id)}
                  className={`inline-flex min-h-[40px] items-center gap-2 rounded-full border px-4 text-sm disabled:opacity-40 ${
                    theme === th.id ? "border-brand" : "border-white/15"
                  }`}
                >
                  <span className="h-3.5 w-3.5 rounded-full" style={{ background: th.swatch }} />
                  {th.label}
                </button>
              ))}
            </div>
          </div>
          <Button
            data-testid="save-preferences-button" onClick={save}
            className="min-h-[48px] rounded-full bg-brand px-7 hover:bg-brand-hover active:scale-95"
          >
            Save preferences
          </Button>
        </div>
        </div>
      </Section>

      <Section title="Your plan" note="What you are on, who is on it with you, and what you have paid."
               testId="section-plan">
        {team && (
          <div className="rounded-3xl border border-white/10 bg-panel p-8" data-testid="team-card">
            <h2 className="inline-flex items-center gap-2 font-display text-2xl font-bold tracking-tight">
              <Users className="h-5 w-5 text-forest" /> {team.team.name}
            </h2>
            <p className="mt-1 text-sm text-ink-soft" data-testid="team-seats">
              {team.seats_used} of {team.team.seats} seats used · one invoice · renews{" "}
              {team.team.renews_at ? new Date(team.team.renews_at).toLocaleDateString() : "—"}
            </p>
            {team.is_owner && (
              <div className="mt-5 flex flex-wrap gap-2">
                <Input
                  data-testid="team-invite-input"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@company.com"
                  className="h-11 flex-1 rounded-xl"
                />
                <Button
                  data-testid="team-invite-button"
                  onClick={invite}
                  className="min-h-[44px] rounded-full bg-brand px-5 hover:bg-brand-hover active:scale-95"
                >
                  <UserPlus className="me-2 h-4 w-4" /> Add seat
                </Button>
              </div>
            )}

            <ul className="mt-5 space-y-2 text-sm" data-testid="team-members">
              <li className="flex items-center justify-between rounded-2xl bg-page px-4 py-2.5">
                <span>{team.owner_email} · owner</span>
              </li>
              {team.members.map((m) => (
                <li
                  key={m.email}
                  className="flex items-center justify-between gap-3 rounded-2xl bg-page px-4 py-2.5"
                >
                  <span>
                    {m.email} · {m.status}
                  </span>
                  <span className="flex items-center gap-2">
                    {team.is_owner && m.invite_token && (
                      <button
                        data-testid={`copy-invite-${m.email}`}
                        title="Copy one-click join link"
                        onClick={() => {
                          navigator.clipboard?.writeText(
                            `${window.location.origin}/invite/team/${m.invite_token}`
                          );
                          toast.success("Team invite link copied");
                        }}
                        className="rounded-full bg-brand/20 px-3 py-1 text-xs text-brand hover:bg-brand/30"
                      >
                        Copy link
                      </button>
                    )}
                    {team.is_owner && (
                      <button data-testid={`remove-member-${m.email}`} onClick={() => removeMember(m.email)}>
                        <Trash2 className="h-4 w-4 text-ink-soft hover:text-destructive" />
                      </button>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="rounded-3xl border border-white/10 bg-panel p-8">
          <p className="text-sm text-ink-soft">
            You are on the <b>{user?.plan}</b> plan.{" "}
            <Link to="/pricing" data-testid="settings-see-plans" className="text-brand underline-offset-4 hover:underline">
              See the plans
            </Link>
          </p>
        {history.length > 0 && (
          <div className="mt-6" data-testid="billing-history">
            <p className="text-sm font-semibold">Billing history</p>
            <ul className="mt-2 space-y-2 text-sm text-ink-soft">
              {history.map((h) => (
                <li key={h.session_id} className="flex justify-between gap-3">
                  <span>{h.plan_id} · ${h.amount}</span>
                  <span>{h.payment_status}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        </div>
      </Section>

      <Section title="Badges" note="Small marks for using ChatMiLu, not for paying for it."
               testId="section-badges">
        <BadgeShelf />
      </Section>

      <Section title="Bring friends" note="Both of you get free Premium when someone joins through you."
               testId="section-friends">
        {referrals && (
          <div className="rounded-3xl border border-white/10 bg-panel p-8" data-testid="referral-card">
            <h2 className="font-display text-2xl font-bold tracking-tight">Invite Friends, Earn Premium</h2>
            <p className="mt-1 text-sm text-ink-soft">
              You and your friend each get {referrals.reward_days} free days of Premium when they sign up.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <code
                data-testid="referral-code"
                className="rounded-full border border-brand/40 bg-brand/10 px-4 py-2 font-mono text-sm"
              >
                {referrals.referral_code}
              </code>
              <Button
                data-testid="copy-referral-link"
                variant="outline"
                className="min-h-[40px] rounded-full active:scale-95"
                onClick={() => {
                  navigator.clipboard?.writeText(
                    `${window.location.origin}/signup?ref=${referrals.referral_code}`
                  );
                  toast.success("Invite link copied");
                }}
              >
                <Gift className="me-2 h-4 w-4" /> Copy invite link
              </Button>
            </div>
            <p className="mt-4 text-sm text-ink-soft" data-testid="referral-stats">
              {referrals.invited_count} friend{referrals.invited_count === 1 ? "" : "s"} joined ·{" "}
              {referrals.months_earned} Premium month{referrals.months_earned === 1 ? "" : "s"} earned
              {referrals.plan_expires
                ? ` · your Premium runs until ${new Date(referrals.plan_expires).toLocaleDateString()}`
                : ""}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {refInviteCount > 1 ? (
                <Textarea
                  data-testid="referral-email-input"
                  value={referralEmail}
                  onChange={(e) => setReferralEmail(e.target.value)}
                  rows={3}
                  placeholder="Paste emails — commas, spaces or new lines"
                  className="min-h-[84px] flex-1 rounded-xl"
                />
              ) : (
                <Input
                  data-testid="referral-email-input"
                  value={referralEmail}
                  onChange={(e) => setReferralEmail(e.target.value)}
                  placeholder="friend@email.com — or paste a whole list"
                  className="h-11 flex-1 rounded-xl"
                />
              )}
              <Button
                data-testid="referral-email-button"
                onClick={sendReferralInvite}
                disabled={invitingRef || !refInviteCount}
                className="min-h-[44px] rounded-full bg-brand px-5 hover:bg-brand-hover active:scale-95"
              >
                <Send className="me-2 h-4 w-4" />
                {invitingRef
                  ? "Sending…"
                  : refInviteCount > 1
                  ? `Invite ${refInviteCount} people`
                  : "Invite by email"}
              </Button>
            </div>
            {!emailReady && (
              <p className="mt-2 text-xs text-ink-soft" data-testid="email-not-configured-note">
                Email delivery isn't connected yet — invites copy the link instead. Add your SendGrid key to send
                real emails.
              </p>
            )}

          </div>
        )}

        <ShareTour />

        <ReferralBoard />
      </Section>

      <Section title="Privacy & data" note="Your account, your export, your delete button."
               testId="section-privacy">
        <div className="rounded-3xl border border-white/10 bg-panel p-8">
        <div className="mt-10 rounded-2xl bg-page p-5">
          <p className="inline-flex items-center gap-2 text-sm font-semibold">
            <ShieldCheck className="h-4 w-4 text-forest" /> Privacy & GDPR
          </p>
          <p className="mt-2 text-sm text-ink-soft">
            Your account: <b>{user?.email}</b> · plan <b>{user?.plan}</b> <MyBadges withWords /> · signed in with{" "}
            <b>{user?.auth_provider}</b>. Chats are stored only to power your history and personalisation, are never
            sold, and are deleted with your account. Delete a conversation any time from the AI Chat sidebar.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Button
              data-testid="export-data-button"
              variant="outline"
              onClick={exportData}
              className="min-h-[40px] rounded-full active:scale-95"
            >
              <Download className="me-2 h-4 w-4" /> Export my data
            </Button>
            <Input
              data-testid="delete-confirm-input"
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value.toUpperCase())}
              placeholder="Type DELETE"
              className="h-10 w-32 rounded-full"
            />
            <Button
              data-testid="delete-account-button"
              onClick={deleteAccount}
              className="min-h-[40px] rounded-full bg-destructive px-5 hover:bg-destructive/90 active:scale-95"
            >
              <Trash2 className="me-2 h-4 w-4" /> Delete everything
            </Button>
          </div>
        </div>
        </div>
      </Section>

      <Section title="Help" note="Tell us what is wrong or what would be better." testId="section-help">
      <div className="rounded-3xl border border-white/10 bg-panel p-8">
        <h2 className="font-display text-2xl font-bold tracking-tight">{t("feedback")} & Support</h2>
        <p className="mt-1 text-sm text-ink-soft">Report an issue or suggest an improvement — we read everything.</p>
        <div className="mt-6 space-y-5">
          <div>
            <Label>Category</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {["general", "bug", "translation", "billing"].map((c) => (
                <button
                  key={c}
                  data-testid={`feedback-category-${c}`}
                  onClick={() => setFb({ ...fb, category: c })}
                  className={`min-h-[36px] rounded-full px-4 text-sm ${
                    fb.category === c ? "bg-brand text-white" : "bg-surfacealt text-ink-soft"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Rating</Label>
            <div className="mt-2 flex gap-1.5">
              {[1, 2, 3, 4, 5].map((r) => (
                <button key={r} data-testid={`feedback-rating-${r}`} onClick={() => setFb({ ...fb, rating: r })}>
                  <Star className={`h-6 w-6 ${r <= fb.rating ? "fill-brand text-brand" : "text-white/20"}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Message</Label>
            <Textarea
              data-testid="feedback-message-input" dir="auto" value={fb.message}
              onChange={(e) => setFb({ ...fb, message: e.target.value })}
              className="mt-1.5 min-h-[120px] rounded-2xl"
            />
          </div>
          <Button
            data-testid="submit-feedback-button" onClick={sendFeedback}
            className="min-h-[48px] rounded-full bg-forest px-7 text-page hover:bg-forest/90 active:scale-95"
          >
            Send feedback
          </Button>
          <p className="text-xs text-ink-soft">
            Prefer email? Write to support@chatmilu.com — Premium plans get priority live support.
          </p>
        </div>
      </div>
      </Section>
    </div>
  );
}
